/**
 * Početna (APP1) curation, per the Q22 defaults: what "Započni prvu sesiju" plays, where the
 * need chips lead, what "Kratka pauza za sebe" holds, and the three images.
 *
 * Changing the curation is an edit here only; the page reads nothing else.
 */
import type { ComponentType } from 'react';
import { Leaf, Moon, Sun } from 'lucide-react';
import { LotusIcon } from '@/components/ui/lotus-icon';
import type { HomeTranslationKey } from '@/utils/translations';
import type { FirstSessionRule, ShortBreakRule } from '@/utils/homeCuration';

export interface HomeNeed {
  /** Chip label (Luka's APP1 wording). */
  labelKey: HomeTranslationKey;
  icon: ComponentType<{ className?: string; 'aria-hidden'?: boolean | 'true' | 'false' }>;
  /**
   * The Vođene group the chip opens (`/sessions?category=<key>`). These are A2's interim groups
   * (`docs/redesign-2026-10/interim-categories.md`); update them with Luka's own layout (Q19).
   */
  groupKey: string;
}

export interface HomeCuration {
  firstSession: FirstSessionRule;
  needs: readonly HomeNeed[];
  shortBreak: ShortBreakRule;
}

export const HOME_CURATION: HomeCuration = {
  // The only free session (lukaaji_quick), retitled "Trenutak prisutnosti – SOS" by C1.
  firstSession: { sessionId: 'd4bef24e-fe19-4085-a5cd-b2f3ee882414', fallbackCategory: 'lukaaji_quick' },
  needs: [
    { labelKey: 'needPeace', icon: LotusIcon, groupKey: 'inner_peace' },
    { labelKey: 'needSleep', icon: Moon, groupKey: 'sleep_rest' },
    { labelKey: 'needRelief', icon: Leaf, groupKey: 'emotions' },
    { labelKey: 'needConfidence', icon: Sun, groupKey: 'self_confidence' },
  ],
  // Sessions of at most 5 displayed minutes (A2's SOS bucket), in catalogue order.
  shortBreak: { limit: 12 },
};

export interface HomeImage {
  /**
   * A photo under `public/assets/` (e.g. `/assets/home/hero.jpg`), or null to show the placeholder.
   * Luka's photos are not delivered yet (R13): no stock photos and no legacy images in the meantime.
   */
  src: string | null;
  /** CSS background in the S1 palette (`public-site/assets/css/style.css`), shown while `src` is null. */
  placeholder: string;
}

export type HomeImageSlot = 'hero' | 'acoustic' | 'guided';

// S1 palette: navy #02344a / #003248, leaf #004d71, teal #06706d, green #17755a, olive #619435,
// gold #f6a80b, gold-light #f9c152.
export const HOME_IMAGES: Record<HomeImageSlot, HomeImage> = {
  // APP1: a landscape at sunset.
  hero: {
    src: null,
    placeholder:
      'radial-gradient(circle at 80% 96%, #f9c152 0, #f6a80b 7%, rgba(246, 168, 11, 0.35) 20%, rgba(246, 168, 11, 0) 42%), ' +
      'linear-gradient(180deg, #003248 0%, #004d71 55%, #06706d 100%)',
  },
  // APP1: the sea with a wave of light.
  acoustic: {
    src: null,
    placeholder:
      'radial-gradient(ellipse at 50% 115%, rgba(249, 193, 82, 0.6) 0, rgba(249, 193, 82, 0) 60%), ' +
      'linear-gradient(180deg, #02344a 0%, #004d71 55%, #06706d 100%)',
  },
  // APP1: a forest stream.
  guided: {
    src: null,
    placeholder:
      'radial-gradient(ellipse at 25% 0%, rgba(249, 193, 82, 0.45) 0, rgba(249, 193, 82, 0) 55%), ' +
      'linear-gradient(165deg, #17755a 0%, #619435 50%, #06706d 100%)',
  },
};
